/**
 * The Camera calibration card: the colorimetric calibration the measured
 * colours are converted through, and its facts.
 *
 * A calibration is immutable once created; the facts (illuminant, camera,
 * illumination, polynomial order, fit and leave-one-out accuracy in ΔE00,
 * creation date) are read from the row and shown, not edited.
 */
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ChoiceCard from "./ChoiceCard";

const number = (value, digits = 2) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : null;
const date = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
};

const FACT_ROWS = [
  ["illuminant_name", "Illuminant", (value) => (value ? String(value) : null)],
  ["camera_device", "Camera", (value) => (value ? String(value) : null)],
  ["illumination_device", "Illumination", (value) => (value ? String(value) : null)],
  ["function_order", "Polynomial order", (value) => (typeof value === "number" ? String(value) : null)],
  ["mean_delta_e_00", "Mean ΔE00", (value) => number(value)],
  ["max_delta_e_00", "Max ΔE00", (value) => number(value)],
  ["loo_mean_delta_e_00", "Leave-one-out mean ΔE00", (value) => number(value)],
  ["loo_max_delta_e_00", "Leave-one-out max ΔE00", (value) => number(value)],
  ["created_at", "Created", date],
];

/** The facts of a calibration row that are set, formatted. */
export function calibrationFacts(row) {
  if (!row) return [];
  return FACT_ROWS.map(([key, label, format]) => ({ key, label, value: format(row[key]) })).filter((fact) => fact.value !== null);
}

/** "cal-d55 • mean ΔE00 1.23" for the card summary. */
export function calibrationSummary(row) {
  if (!row) return "";
  const mean = number(row.mean_delta_e_00);
  return mean === null ? row.name : `${row.name} • mean ΔE00 ${mean}`;
}

export default function CalibrationCard({ rows, valueId, onChange }) {
  const row = rows.find((candidate) => candidate.id === valueId) || null;
  const facts = calibrationFacts(row);

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      <ChoiceCard
        inputId="exploration-calibration"
        label="Camera calibration name"
        rows={rows}
        valueId={valueId}
        onChange={onChange}
        manage={{
          text: "New calibrations are created and evaluated on the",
          linkLabel: "Camera calibrations",
          route: "/cameracalibrations",
        }}
      />
      {facts.length > 0 ? (
        <Box
          data-testid="calibration-facts"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f6f8fb",
          }}
        >
          {facts.map((fact) => (
            <Box key={fact.key}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {fact.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fact.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}
